/**
 * Accessibility the framework provides rather than each component: ids that
 * pair a label with its control on both sides of hydration, one live region
 * for announcements, and focus kept inside a dialog and given back after it.
 */
import { hasGlobal } from '../ir/guards';

// ---------------------------------------------------------------------------
// ids
// ---------------------------------------------------------------------------

/** Where ids are drawn from while a server render or a hydration walks a page in document order. */
interface IdScope {
  next: number;
}

let scope: IdScope | null = null;
let browserNext = 0;

/**
 * Runs `fn` drawing ids from `into`: the server renderer does this for a
 * render, and a hydration for its walk, so both number the same page alike.
 */
export function withIdScope<T>(into: IdScope, fn: () => T): T {
  const previous = scope;
  scope = into;
  try {
    return fn();
  } finally {
    scope = previous;
  }
}

/** A fresh counter for one render or one hydration. */
export function idScope(): IdScope {
  return { next: 0 };
}

/**
 * An id, fixed the first time it is read.
 *
 * Read it wherever it is needed — `for` on a label and `id` on its input, in
 * a template — and each read gives the same string. It is numbered where it
 * is first written: a server render and the hydration of its page reach it in
 * the same place in document order, so they agree, rows of a list included.
 * Content built only in the browser takes ids of its own (`field-b3`), which
 * never collide with the server's.
 */
export interface Id {
  (): string;
  toString(): string;
}

export function id(prefix = 'c'): Id {
  // Numbered once per scope: a server render that waits for data and renders
  // again numbers its kept pass afresh, in that pass's document order.
  const byScope = new WeakMap<IdScope, string>();
  let last: string | null = null;
  const read = (): string => {
    if (scope) {
      let value = byScope.get(scope);
      if (value === undefined) {
        value = `${prefix}-${scope.next++}`;
        byScope.set(scope, value);
      }
      last = value;
      return value;
    }
    // Outside a render or a hydration — a binding running again later — the
    // id is what it was last given; content built only in the browser gets one of its own.
    return (last ??= `${prefix}-b${browserNext++}`);
  };
  return Object.assign(read, { toString: read });
}

// ---------------------------------------------------------------------------
// announcements
// ---------------------------------------------------------------------------

export type Politeness = 'polite' | 'assertive';

const HIDDEN_STYLE =
  'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';

/** The page's live region for `politeness`: the one the server wrote, or one made once. */
function liveRegion(politeness: Politeness): HTMLElement {
  const existing = document.querySelector<HTMLElement>(`[data-c-live="${politeness}"]`);
  if (existing) return existing;
  const region = document.createElement('div');
  region.setAttribute('data-c-live', politeness);
  region.setAttribute('aria-live', politeness);
  region.setAttribute('aria-atomic', 'true');
  region.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
  region.setAttribute('style', HIDDEN_STYLE);
  document.body.appendChild(region);
  return region;
}

/**
 * Says `message` to assistive technology, through one live region per page.
 * The region is emptied first and filled a frame later, so repeating the same
 * message is announced again.
 */
export function announce(message: string, options: { politeness?: Politeness } = {}): void {
  if (!(hasGlobal('document'))) return;
  const region = liveRegion(options.politeness ?? 'polite');
  region.textContent = '';
  const write = (): void => {
    region.textContent = message;
  };
  if (hasGlobal('requestAnimationFrame')) requestAnimationFrame(write);
  else setTimeout(write, 0);
}

// ---------------------------------------------------------------------------
// focus
// ---------------------------------------------------------------------------

/**
 * After a client-side navigation: moves focus to the new page and announces
 * its title, as a full page load would for a screen reader. Focus goes to the
 * page's first heading, or to `region` itself; either is given
 * `tabindex="-1"` so it takes focus without joining the Tab order.
 */
export function focusNewPage(region: HTMLElement, title: string = (hasGlobal('document') ? document.title : '')): void {
  const target = region.querySelector<HTMLElement>('h1') ?? region.querySelector<HTMLElement>('h2, h3') ?? region;
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  const name = title || target.textContent?.trim() || '';
  if (name) announce(name);
}

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

/** The elements in `root` that Tab reaches, in order. */
export function focusables(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.getAttribute('tabindex') !== '-1' && !element.hasAttribute('inert') && !element.closest('[inert]'),
  );
}

export interface TrapFocusOptions {
  /** Where focus goes now; the first thing Tab reaches in the element by default. */
  readonly initial?: HTMLElement;
  /** Where focus goes back to on release; whatever had it when the trap was set by default. */
  readonly returnTo?: HTMLElement | null;
}

/**
 * Keeps Tab and Shift+Tab inside `element`, moving focus into it now. The
 * returned release lets go and gives focus back to whatever had it before —
 * the button that opened the dialog, typically.
 */
export function trapFocus(element: HTMLElement, options: TrapFocusOptions = {}): () => void {
  const opener =
    options.returnTo !== undefined
      ? options.returnTo
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;
    const items = focusables(element);
    if (items.length === 0) {
      event.preventDefault();
      element.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !element.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !element.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', onKeydown, true);
  if (options.initial) {
    if (options.initial === element && !element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    options.initial.focus();
  } else if (!element.contains(document.activeElement)) {
    const target = focusables(element)[0] ?? element;
    if (target === element && !element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    target.focus();
  }
  return () => {
    document.removeEventListener('keydown', onKeydown, true);
    if (opener?.isConnected) opener.focus();
  };
}
